import { Box, Typography, Drawer, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';

import { drawerWidth, collapsedDrawerWidth, primaryColor } from '../../utils/theme'; // Adjust import path if needed


export const LogoContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: collapsed ? 'center' : 'flex-start',
  padding: collapsed ? theme.spacing(0.5, 1) : theme.spacing(1, 3), // More compact padding
  width: collapsed ? `${collapsedDrawerWidth}px` : `${drawerWidth}px`,
  height: '64px', // Reduced height to match standard Navbar 64px
  backgroundColor: theme.palette.custom.sidebarBg,
  transition: theme.transitions.create(['width', 'padding'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),

  // CRITICAL: Fixed positioning to stay above the scrolling navigation list 
  position: 'fixed',
  top: 0,
  left: 0,
  zIndex: theme.zIndex.drawer,

  // Match the border style of the Navbar
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

export const LogoText = styled(Typography)({
  fontWeight: 600,
  fontSize: '1rem', // Reduced from 1.2rem
  color: '#333333',
  lineHeight: 1, // Compact typography
});

export const OrgText = styled(Typography)({
  fontSize: '0.65rem', // Reduced from 0.75rem
  color: '#777', // Lighter color for 'Organization'
  lineHeight: 1,
  marginTop: '2px',
});

export const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  width: collapsed ? collapsedDrawerWidth : drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  '& .MuiDrawer-paper': {
    width: collapsed ? collapsedDrawerWidth : drawerWidth,
    boxSizing: 'border-box',
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    // CRITICAL: Push the drawer content down below the fixed logo container
    marginTop: '64px', // Reduced to 64px to match LogoContainer
    height: 'calc(100% - 64px)', // Adjusted to match marginTop
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
  },
}));

// The rest of your ListItemButton, ListItemIcon, etc., styling remains the same 
// and uses the theme variables correctly.
// ... (StyledListItemButton, StyledListItemIcon, ActiveParentText, ActiveChildItem, ChildItem) ...

// You must redefine these if you deleted them previously:
export const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1, 4), // Reduced from 1.5
  color: theme.palette.custom.sidebarText,
  justifyContent: 'flex-start', // Ensure standard alignment
  minHeight: 40, // Reduced from 48

  '& .MuiListItemText-primary': {
    fontSize: '0.9rem', // Slightly increased from 0.8rem
    fontWeight: 400,
  },
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-selected': {
    backgroundColor: 'transparent',
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  }
}));
export const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  // Consistent spacing for the icon area
  minWidth: 32, // Reduced from 40
  color: theme.palette.custom.iconColor,
  justifyContent: 'center',

  // Style to ensure both <svg> and <img> icons are the correct size and color
  '& svg, & img': {
    height: '16px', // Reduced from 18px
    width: '16px',
    color: 'inherit', // Icons inherit the color set on the parent
  },
  // Style for the expand/collapse icons (ExpandLess/ExpandMore)
  '& .MuiSvgIcon-root': {
    fontSize: '16px', // Reduced from 18px
    color: theme.palette.grey[500],
  },
}));
export const ActiveParentText = styled(ListItemText)(({ theme }) => ({ /* ... styles ... */ }));

export const ChildItem = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(0, 3, 0, 3.5),
  color: theme.palette.custom.sidebarText,
  marginLeft: theme.spacing(1),
  paddingLeft: theme.spacing(7), // Reduced from 8
  width: `calc(100% - ${theme.spacing(1)} * 2)`, // Adjust width due to margin

  '& .MuiListItemText-primary': {
    fontSize: '0.875rem', // Slightly increased from 0.8rem
    fontWeight: 400,
  },
  '&:hover': {
    backgroundColor: 'transparent', // No background change on hover for inactive child
  },
  '&.Mui-selected': {
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
    backgroundColor: theme.palette.custom.activeBg,
    borderLeft: `4px solid ${primaryColor} !important`,
    fontWeight: 600,
    position: 'relative',
    paddingLeft: theme.spacing(6.5), // Reduced from 7.5
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  }
}));

export const ParentListItemButton = styled(ListItemButton)(({ theme }) => ({
  // Standard padding matching the screenshot layout
  padding: theme.spacing(1, 3), // Reduced from 1.5
  color: theme.palette.custom.sidebarText,
  borderRadius: '0px',

  // Active Parent State (e.g., Management)
  '&.Mui-selected': {
    backgroundColor: theme.palette.custom.activeBg, // Light Green/Grey from theme
    fontWeight: 600,
    '& .MuiListItemIcon-root': {
      color: theme.palette.custom.sidebarText, // Icon color matches text color in active state
    },
    '&:hover': {
      backgroundColor: theme.palette.custom.activeBg,
    },
  },

  // Hover State
  '&:hover': {
    backgroundColor: theme.palette.grey[50], // Very light hover
  },
}));
